import {
	EmailOutlineIcon,
	FacebookIcon,
	FacebookMessengerIcon,
	InstagramIcon,
	LineIcon,
	LinkIcon,
	NextDoorIcon,
	RedditIcon,
	ShareIcon,
	SmsIcon,
	TikTokIcon,
	TwitterIcon,
	VkIcon,
	WhatsAppIcon,
} from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import type { ShareAction } from 'src/app/shared/hooks/share';

import type { ShareChannel } from './types';

export type ShareVariant =
	| 'facebook'
	| 'instagram'
	| 'twitter'
	| 'whatsapp'
	| 'facebookMessenger'
	| 'nextdoor'
	| 'reddit'
	| 'default';

type ShareButtonConfig = Readonly<{
	icon: React.ReactElement;
	label: TranslationKey;
	variant?: ShareVariant;
	successLabel?: TranslationKey;
	secondaryLabel?: TranslationKey;
}>;

const CHANNEL_ICONS = {
	facebook: FacebookIcon,
	instagram: InstagramIcon,
	twitter: TwitterIcon,
	whatsapp: WhatsAppIcon,
	facebookMessenger: FacebookMessengerIcon,
	nextdoor: NextDoorIcon,
	reddit: RedditIcon,
	line: LineIcon,
	tiktok: TikTokIcon,
	vk: VkIcon,
	email: EmailOutlineIcon,
	sms: SmsIcon,
	link: LinkIcon,
	share: ShareIcon,
} as const;

const createShareButtonConfig = (
	iconKey: keyof typeof CHANNEL_ICONS,
	label: TranslationKey,
	variant: ShareVariant = 'default',
	options?: {
		successLabel?: TranslationKey;
		secondaryLabel?: TranslationKey;
		iconProps?: Record<string, unknown>;
	},
): ShareButtonConfig => {
	const IconComponent = CHANNEL_ICONS[iconKey];
	return {
		icon: <IconComponent {...(options?.iconProps || {})} />,
		label,
		variant,
		...(options?.successLabel && { successLabel: options.successLabel }),
		...(options?.secondaryLabel && { secondaryLabel: options.secondaryLabel }),
	};
};

export const SHARE_BUTTON_CONFIG: Readonly<Record<ShareChannel, ShareButtonConfig>> = {
	facebook: createShareButtonConfig(
		'facebook',
		translationKey('fe.components.psf.share.buttons.short.facebook'),
		'facebook',
	),
	facebookMessenger: createShareButtonConfig(
		'facebookMessenger',
		translationKey('fe.components.psf.share.buttons.short.facebook_messenger'),
		'facebookMessenger',
	),
	twitter: createShareButtonConfig(
		'twitter',
		translationKey('fe.components.psf.share.buttons.short.twitter'),
		'twitter',
	),
	whatsapp: createShareButtonConfig(
		'whatsapp',
		translationKey('fe.components.psf.share.buttons.short.whatsapp'),
		'whatsapp',
	),
	instagram: createShareButtonConfig(
		'instagram',
		translationKey('fe.components.psf.share.buttons.short.instagram'),
		'instagram',
	),
	line: createShareButtonConfig('line', translationKey('fe.components.psf.share.buttons.short.line')),
	email: createShareButtonConfig('email', translationKey('fe.components.psf.share.buttons.short.email')),
	sms: createShareButtonConfig('sms', translationKey('fe.components.psf.share.buttons.short.sms')),
	copy: createShareButtonConfig('link', translationKey('fe.components.psf.share.buttons.copy_link'), 'default', {
		successLabel: translationKey('fe.components.share_buttons.copied'),
		secondaryLabel: translationKey('corgi.psf.share.buttons.share_link_label'),
	}),
	vk: createShareButtonConfig('vk', translationKey('fe.components.psf.share.buttons.short.vk')),
	nextdoor: createShareButtonConfig('nextdoor', translationKey('corgi.psf.share.buttons.short.nextdoor'), 'nextdoor'),
	mobileNativeShare: createShareButtonConfig('share', translationKey('corgi.psf.share.buttons.short.mobile_share')),
	reddit: createShareButtonConfig('reddit', translationKey('corgi.psf.share.buttons.short.reddit'), 'reddit'),
	instagramStories: createShareButtonConfig(
		'instagram',
		translationKey('corgi.psf.share.buttons.short.instagram_stories'),
	),
	tiktok: createShareButtonConfig('tiktok', translationKey('corgi.psf.share.buttons.short.tiktok')),
};

export type ShareActionItem = Readonly<{
	action: () => Promise<boolean>;
	channel: ShareChannel;
	icon: React.ReactElement;
	label: TranslationKey;
	variant?: ShareVariant;
	successLabel?: TranslationKey;
	secondaryLabel?: TranslationKey;
	url: string;
}>;

type Result = readonly ShareActionItem[];

export function mapButtonConfigs(shareActions: ReadonlyArray<ShareAction<ShareChannel>>): Result {
	return shareActions.map(({ action, channel, url }) => ({
		action,
		channel,
		url,
		...SHARE_BUTTON_CONFIG[channel],
	}));
}
